import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import axios from 'axios';
import { devError } from '../lib/devLog';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          devError('Auth verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const loginWithGoogle = useCallback(async (sessionData) => {
    // Store JWT token and user data from Google OAuth exchange
    const { access_token, user: userData } = sessionData;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    // Unregister FCM token before clearing auth (best-effort, fire-and-forget)
    try {
      import('../lib/push').then(m =>
        m.unregisterPushNotifications().catch(err => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('FCM unregister failed on logout (non-fatal):', err);
          }
        })
      );
    } catch (importErr) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('push module import failed on logout (non-fatal):', importErr);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    // Also clear OAuth session cookie
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }).catch(err => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Logout API call failed (non-fatal):', err);
      }
    });
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    isAuthenticated: !!user,
    isUser: user?.role === 'user',
    isDriver: user?.role === 'driver',
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
